import type { Vehicle } from '../types';

export interface VehicleMatch {
  vehicle: Vehicle;
  /** 0-1 overall match score. */
  matchConfidence: number;
  /** Human-readable reasons for the match. */
  matchReasons: string[];
}

export interface VehicleMatchResult {
  /**
   * A vehicle where year, make, AND model all match exactly.
   * When set, the caller should auto-select without prompting.
   */
  perfectMatch: Vehicle | null;
  /**
   * Vehicles where at least 2 of 3 fields match.
   * Sorted by score descending. Shown as a recommendation list.
   */
  closeMatches: VehicleMatch[];
}

/**
 * Three-tier vehicle matching for AI-extracted data:
 *
 * 1. **Perfect match** — year + make + model all match → auto-select
 * 2. **Close match** — at least 2 of 3 fields match → show list
 * 3. **No match** — nothing returned
 */
class VehicleMatcher {
  /**
   * Find matching vehicles from a customer's vehicle list.
   */
  static findMatches(
    vehicles: Vehicle[],
    year: string | number,
    make: string,
    model: string,
  ): VehicleMatchResult {
    if (vehicles.length === 0) {
      return { perfectMatch: null, closeMatches: [] };
    }

    const yearNum = typeof year === 'string'
      ? parseInt(year.replace(/,/g, ''), 10)
      : year;
    const makeLower = (make || '').toLowerCase().trim();
    const modelLower = (model || '').toLowerCase().trim();

    const scored: VehicleMatch[] = vehicles.map((v) => {
      let score = 0;
      const reasons: string[] = [];
      let fieldsMatched = 0;

      // --- Year (exact only) ---
      if (yearNum && !isNaN(yearNum) && v.year === yearNum) {
        score += 0.34;
        fieldsMatched++;
        reasons.push('year matches');
      }

      // --- Make (case-insensitive) ---
      const vMake = (v.make || '').toLowerCase();
      if (makeLower && vMake === makeLower) {
        score += 0.33;
        fieldsMatched++;
        reasons.push('make matches');
      } else if (makeLower && (vMake.includes(makeLower) || makeLower.includes(vMake))) {
        score += 0.2;
        reasons.push('make similar');
      }

      // --- Model (case-insensitive) ---
      const vModel = (v.model || '').toLowerCase();
      if (modelLower && vModel === modelLower) {
        score += 0.33;
        fieldsMatched++;
        reasons.push('model matches');
      } else if (modelLower && (vModel.includes(modelLower) || modelLower.includes(vModel))) {
        score += 0.2;
        reasons.push('model similar');
      }

      return { vehicle: v, matchConfidence: score, matchReasons: reasons, _fieldsMatched: fieldsMatched };
    }) as (VehicleMatch & { _fieldsMatched: number })[];

    // Perfect match: all 3 fields match exactly (score ~1.0)
    const perfectCandidates = (scored as (VehicleMatch & { _fieldsMatched: number })[])
      .filter((m) => m._fieldsMatched === 3);
    const perfectMatch = perfectCandidates.length > 0
      ? perfectCandidates.reduce((a, b) => (b.matchConfidence > a.matchConfidence ? b : a)).vehicle
      : null;

    // Close matches: at least 2 of 3 fields match
    const closeMatches: VehicleMatch[] = (scored as (VehicleMatch & { _fieldsMatched: number })[])
      .filter((m) => m._fieldsMatched >= 2)
      .sort((a, b) => b.matchConfidence - a.matchConfidence)
      .map(({ _fieldsMatched, ...rest }) => rest); // strip internal field

    return { perfectMatch, closeMatches };
  }
}

export default VehicleMatcher;
